/**
 * LayoutCanvasDesigner — Sprint M
 *
 * Route: /dashboard/venues/:venueId/templates/:templateId/designer
 *
 * A canvas-style visual designer that lets organisers place, drag, resize
 * and rotate section blocks on a 100×100 percentage plane. Multi-select
 * supported via `react-selecto`. Each section is a positioned <div> that
 * `react-moveable` attaches handles to when selected.
 *
 * Sprint M requirements delivered here:
 *  - Preset wizard on empty template (theatre / arena / cabaret / blank)
 *  - Add rect / wedge / decorative blocks
 *  - Drag, resize, rotate with snap-to-grid + arrow-key nudge
 *  - Pricing zone manager sidebar
 *  - Section properties panel (right)
 *  - Buyer preview modal (reuses Sprint L overview)
 *  - Debounced auto-save via bulk-update; explicit Save button too
 *  - Live-template guard (attached_to_live_event blocks structural edits;
 *    one-click Duplicate button lands you in the new template's designer)
 *  - Undo/redo (last 20 in-memory states)
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Moveable from "react-moveable";
import Selecto from "react-selecto";
import {
  Undo2, Redo2, Save, Eye, Copy, ArrowLeft, Loader2, Lock, Sparkles, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { venuesApi } from "../../lib/events";
import { extractError } from "../../lib/api";
import PricingZoneManager from "../../components/designer/PricingZoneManager";
import SectionPropertiesPanel from "../../components/designer/SectionPropertiesPanel";
import { AddSectionToolbar, PresetWizard } from "../../components/designer/DesignerToolbox";
import BuyerPreviewModal from "../../components/designer/BuyerPreviewModal";

// SVG canvas viewport
const VW = 100;
const VH = 100;
const GRID = 1;   // 1% snap grid
const NUDGE = 1;  // 1% arrow-key nudge

// Local storage key for undo stack (in-memory, per-tab)
const clamp = (n, min, max) => Math.min(max, Math.max(min, n));
const round = (n) => Math.round(n * 10) / 10;

function useDebouncedCallback(fn, delayMs = 500) {
  const t = useRef(null);
  useEffect(() => () => t.current && clearTimeout(t.current), []);
  return useCallback((...args) => {
    if (t.current) clearTimeout(t.current);
    t.current = setTimeout(() => fn(...args), delayMs);
  }, [fn, delayMs]);
}

export default function LayoutCanvasDesigner() {
  const { venueId, templateId } = useParams();
  const navigate = useNavigate();

  const [template, setTemplate] = useState(null);
  const [sections, setSections] = useState([]);
  const [zones, setZones] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [attachedToLive, setAttachedToLive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);

  // Undo/redo stacks store lightweight geometry snapshots
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);
  const suppressHistoryRef = useRef(false);

  const canvasRef = useRef(null);
  const moveableRef = useRef(null);

  const disabled = attachedToLive;

  // ------------------------------------------------------------------
  // Load
  // ------------------------------------------------------------------
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [detail, zonesRes] = await Promise.all([
        venuesApi.getTemplate(templateId),
        venuesApi.listPricingZones(templateId),
      ]);
      setTemplate(detail.template);
      setSections(detail.sections || []);
      setZones(zonesRes || []);
      setAttachedToLive(!!detail.attached_to_live_event);
      // If it's a brand-new template with no sections and no zones, open the
      // preset wizard so the organiser starts from a smart default.
      if ((detail.sections || []).length === 0 && !detail.attached_to_live_event) {
        setWizardOpen(true);
      }
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setLoading(false);
    }
  }, [templateId]);

  useEffect(() => { load(); }, [load]);

  // ------------------------------------------------------------------
  // History
  // ------------------------------------------------------------------
  const pushHistory = useCallback((snap) => {
    if (suppressHistoryRef.current) return;
    setUndoStack((s) => [...s.slice(-19), snap]);
    setRedoStack([]);
  }, []);

  const snapshot = () => sections.map((s) => ({ ...s }));

  const undo = () => {
    if (undoStack.length === 0) return;
    const prev = undoStack[undoStack.length - 1];
    setRedoStack((s) => [...s.slice(-19), snapshot()]);
    setUndoStack((s) => s.slice(0, -1));
    suppressHistoryRef.current = true;
    setSections(prev);
    setTimeout(() => { suppressHistoryRef.current = false; }, 20);
    debouncedBulkSave(prev);
  };
  const redo = () => {
    if (redoStack.length === 0) return;
    const next = redoStack[redoStack.length - 1];
    setUndoStack((s) => [...s.slice(-19), snapshot()]);
    setRedoStack((s) => s.slice(0, -1));
    suppressHistoryRef.current = true;
    setSections(next);
    setTimeout(() => { suppressHistoryRef.current = false; }, 20);
    debouncedBulkSave(next);
  };

  // ------------------------------------------------------------------
  // Save (debounced bulk update for geometry, per-field for other props)
  // ------------------------------------------------------------------
  const saveBulk = useCallback(async (sectionsSnapshot) => {
    if (disabled) return;
    setSaving(true);
    try {
      const items = sectionsSnapshot.map((s) => ({
        id: s.id,
        patch: {
          x_percent: s.x_percent, y_percent: s.y_percent,
          width_percent: s.width_percent, height_percent: s.height_percent,
          rotation_deg: s.rotation_deg,
          shape_type: s.shape_type,
          label_display: s.label_display,
          pricing_zone_id: s.pricing_zone_id ?? null,
          is_decorative: !!s.is_decorative,
          icon_name: s.icon_name ?? null,
          name: s.name,
          capacity: s.capacity,
        },
      }));
      if (items.length) await venuesApi.bulkUpdateSections(templateId, items);
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setSaving(false);
    }
  }, [templateId, disabled]);

  const debouncedBulkSave = useDebouncedCallback((snap) => saveBulk(snap), 600);

  const patchSection = (id, patch) => {
    setSections((cur) => {
      const before = cur;
      const next = cur.map((s) => (s.id === id ? { ...s, ...patch } : s));
      pushHistory(before);
      debouncedBulkSave(next);
      return next;
    });
  };

  const applyPatchToSelected = (patch) => {
    if (selectedIds.length === 0) return;
    setSections((cur) => {
      const before = cur;
      const next = cur.map((s) => (selectedIds.includes(s.id) ? { ...s, ...patch } : s));
      pushHistory(before);
      debouncedBulkSave(next);
      return next;
    });
  };

  // ------------------------------------------------------------------
  // Add / delete / duplicate section
  // ------------------------------------------------------------------
  const addSection = async (defaults) => {
    if (disabled) return;
    // Unique name — append counter if collision
    const base = defaults.name || "New Section";
    const existing = new Set(sections.map((s) => s.name));
    let name = base, i = 2;
    while (existing.has(name)) name = `${base} ${i++}`;
    try {
      const created = await venuesApi.addSection(templateId, { ...defaults, name });
      setSections((cur) => {
        pushHistory(cur);
        return [...cur, created];
      });
      setSelectedIds([created.id]);
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const duplicateSelected = async () => {
    if (disabled) return;
    for (const id of selectedIds) {
      const s = sections.find((x) => x.id === id);
      if (!s) continue;
      await addSection({
        name: `${s.name} (copy)`,
        section_type: s.section_type,
        capacity: s.capacity,
        shape_type: s.shape_type || "rect",
        x_percent: clamp(round((s.x_percent || 0) + 3), 0, 100 - (s.width_percent || 10)),
        y_percent: clamp(round((s.y_percent || 0) + 3), 0, 100 - (s.height_percent || 10)),
        width_percent: s.width_percent, height_percent: s.height_percent,
        rotation_deg: s.rotation_deg,
        is_decorative: !!s.is_decorative,
        icon_name: s.icon_name,
        pricing_zone_id: s.pricing_zone_id ?? null,
        label_display: s.label_display,
      });
    }
  };

  const deleteSelected = async () => {
    if (disabled || selectedIds.length === 0) return;
    if (!confirm(`Delete ${selectedIds.length} section${selectedIds.length === 1 ? "" : "s"}?`)) return;
    try {
      for (const id of selectedIds) {
        await venuesApi.removeSection(templateId, id);
      }
      setSections((cur) => {
        pushHistory(cur);
        return cur.filter((s) => !selectedIds.includes(s.id));
      });
      setSelectedIds([]);
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  // Keyboard shortcuts (arrows to nudge, del to delete, Ctrl+Z / Y)
  useEffect(() => {
    const onKey = (e) => {
      if (previewOpen || wizardOpen) return;
      if (e.target?.tagName === "INPUT" || e.target?.tagName === "TEXTAREA" || e.target?.tagName === "SELECT") return;
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z" && !e.shiftKey) { e.preventDefault(); undo(); return; }
      if ((e.ctrlKey || e.metaKey) && (e.key.toLowerCase() === "y" || (e.shiftKey && e.key.toLowerCase() === "z"))) { e.preventDefault(); redo(); return; }
      if (e.key === "Delete" || e.key === "Backspace") { e.preventDefault(); deleteSelected(); return; }
      if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) {
        e.preventDefault();
        if (selectedIds.length === 0) return;
        const dx = e.key === "ArrowLeft" ? -NUDGE : e.key === "ArrowRight" ? NUDGE : 0;
        const dy = e.key === "ArrowUp" ? -NUDGE : e.key === "ArrowDown" ? NUDGE : 0;
        setSections((cur) => {
          const before = cur;
          const next = cur.map((s) => selectedIds.includes(s.id) ? {
            ...s,
            x_percent: clamp(round((s.x_percent || 0) + dx), 0, 100 - (s.width_percent || 0)),
            y_percent: clamp(round((s.y_percent || 0) + dy), 0, 100 - (s.height_percent || 0)),
          } : s);
          pushHistory(before);
          debouncedBulkSave(next);
          return next;
        });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds, previewOpen, wizardOpen, undoStack, redoStack]);

  // ------------------------------------------------------------------
  // react-moveable events → convert px deltas back to % of canvas
  // ------------------------------------------------------------------
  const canvasSize = useRef({ width: 1, height: 1 });
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const measure = () => {
      const r = el.getBoundingClientRect();
      canvasSize.current = { width: r.width || 1, height: r.height || 1 };
    };
    measure();
    const obs = new ResizeObserver(measure);
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const targetsFromSelected = () =>
    selectedIds.map((id) => document.querySelector(`[data-section-node="${id}"]`)).filter(Boolean);

  const onDrag = ({ target, beforeTranslate }) => {
    const id = target.getAttribute("data-section-node");
    const sec = sections.find((s) => s.id === id);
    if (!sec) return;
    const dxPct = (beforeTranslate[0] / canvasSize.current.width) * 100;
    const dyPct = (beforeTranslate[1] / canvasSize.current.height) * 100;
    const origin = sec.__dragOrigin || { x: sec.x_percent, y: sec.y_percent };
    if (!sec.__dragOrigin) sec.__dragOrigin = origin;
    const nx = clamp(round(origin.x + dxPct), 0, 100 - (sec.width_percent || 0));
    const ny = clamp(round(origin.y + dyPct), 0, 100 - (sec.height_percent || 0));
    setSections((cur) => cur.map((s) => (s.id === id ? { ...s, x_percent: nx, y_percent: ny, __dragOrigin: origin } : s)));
  };

  const onDragEnd = () => {
    setSections((cur) => {
      const before = cur;
      const next = cur.map((s) => { const c = { ...s }; delete c.__dragOrigin; return c; });
      pushHistory(before);
      debouncedBulkSave(next);
      return next;
    });
  };

  const onResize = ({ target, width, height, drag }) => {
    const id = target.getAttribute("data-section-node");
    const sec = sections.find((s) => s.id === id);
    if (!sec) return;
    const wPct = clamp(round((width / canvasSize.current.width) * 100), 3, 100);
    const hPct = clamp(round((height / canvasSize.current.height) * 100), 3, 100);
    const origin = sec.__resizeOrigin || { x: sec.x_percent, y: sec.y_percent };
    if (!sec.__resizeOrigin) sec.__resizeOrigin = origin;
    const dx = (drag.beforeTranslate[0] / canvasSize.current.width) * 100;
    const dy = (drag.beforeTranslate[1] / canvasSize.current.height) * 100;
    const nx = clamp(round(origin.x + dx), 0, 100 - wPct);
    const ny = clamp(round(origin.y + dy), 0, 100 - hPct);
    setSections((cur) => cur.map((s) =>
      s.id === id ? { ...s, x_percent: nx, y_percent: ny, width_percent: wPct, height_percent: hPct, __resizeOrigin: origin } : s,
    ));
  };

  const onResizeEnd = () => {
    setSections((cur) => {
      const before = cur;
      const next = cur.map((s) => { const c = { ...s }; delete c.__resizeOrigin; return c; });
      pushHistory(before);
      debouncedBulkSave(next);
      return next;
    });
  };

  const onRotate = ({ target, beforeRotate }) => {
    const id = target.getAttribute("data-section-node");
    setSections((cur) => cur.map((s) => (s.id === id ? { ...s, rotation_deg: round(beforeRotate) } : s)));
  };
  const onRotateEnd = () => {
    setSections((cur) => {
      const before = cur;
      pushHistory(before);
      debouncedBulkSave(cur);
      return cur;
    });
  };

  const explicitSave = async () => {
    await saveBulk(sections);
    toast.success("Layout saved");
  };

  // ------------------------------------------------------------------
  // Preset wizard: seed sections in bulk
  // ------------------------------------------------------------------
  const applyPreset = async (preset) => {
    if (disabled) return;
    try {
      // Clear existing sections (only safe when template isn't attached)
      for (const s of sections) {
        // eslint-disable-next-line no-await-in-loop
        await venuesApi.removeSection(templateId, s.id);
      }
      const created = [];
      for (const spec of preset.sections) {
        // eslint-disable-next-line no-await-in-loop
        const s = await venuesApi.addSection(templateId, spec);
        created.push(s);
      }
      setSections(created);
      setSelectedIds([]);
      setWizardOpen(false);
      toast.success(`${preset.label} layout applied`);
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  // ------------------------------------------------------------------
  // Duplicate this template (used when locked to a live event)
  // ------------------------------------------------------------------
  const cloneAndOpen = async () => {
    try {
      const cloned = await venuesApi.cloneTemplate(templateId);
      toast.success(`Cloned to draft: ${cloned.template_name}`);
      navigate(`/dashboard/venues/${cloned.venue_id}/templates/${cloned.id}/designer`);
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  // ------------------------------------------------------------------
  // Selected section shortcuts
  // ------------------------------------------------------------------
  const selectedSection = useMemo(
    () => (selectedIds.length === 1 ? sections.find((s) => s.id === selectedIds[0]) : null),
    [selectedIds, sections],
  );

  const zonesById = useMemo(() => Object.fromEntries(zones.map((z) => [z.id, z])), [zones]);

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  if (loading) {
    return (
      <div className="p-8 flex justify-center" data-testid="designer-loading">
        <Loader2 size={20} className="animate-spin text-[var(--gt-ink-3)]" />
      </div>
    );
  }

  return (
    <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-4" data-testid="layout-canvas-designer">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-3">
          <Link
            to={`/dashboard/venues/${venueId}`}
            className="gt-btn-ghost text-xs"
            data-testid="designer-back-link"
          >
            <ArrowLeft size={12} /> Back
          </Link>
          <div>
            <div className="gt-label text-[10px]">Canvas designer</div>
            <div className="font-heading text-xl font-black tracking-tight" data-testid="designer-template-name">
              {template?.template_name}
            </div>
          </div>
          {attachedToLive && (
            <span
              className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded-sm bg-amber-100 text-amber-800 border border-amber-300"
              data-testid="attached-to-live-badge"
            >
              <Lock size={11} /> Live · read only
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={undo}
            disabled={undoStack.length === 0}
            className="gt-btn-ghost text-xs disabled:opacity-40"
            data-testid="designer-undo-btn"
            aria-label="Undo"
          >
            <Undo2 size={12} /> Undo
          </button>
          <button
            type="button"
            onClick={redo}
            disabled={redoStack.length === 0}
            className="gt-btn-ghost text-xs disabled:opacity-40"
            data-testid="designer-redo-btn"
            aria-label="Redo"
          >
            <Redo2 size={12} /> Redo
          </button>
          <button
            type="button"
            onClick={() => setPreviewOpen(true)}
            className="gt-btn-secondary text-xs"
            data-testid="designer-preview-btn"
          >
            <Eye size={12} /> Preview
          </button>
          {attachedToLive ? (
            <button
              type="button"
              onClick={cloneAndOpen}
              className="gt-btn-primary text-xs"
              data-testid="designer-clone-btn"
            >
              <Copy size={12} /> Duplicate to edit
            </button>
          ) : (
            <button
              type="button"
              onClick={explicitSave}
              disabled={saving}
              className="gt-btn-primary text-xs"
              data-testid="designer-save-btn"
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
              Save
            </button>
          )}
        </div>
      </div>

      {/* Live template banner */}
      {attachedToLive && (
        <div
          className="mb-3 p-3 rounded-sm border border-amber-300 bg-amber-50 text-amber-900 text-xs"
          data-testid="live-template-banner"
        >
          <strong>This template is attached to a live event.</strong> Structural edits are locked to protect
          existing buyers. Click <em>Duplicate to edit</em> above to work on a draft copy — you can then attach
          the new version to future events.
        </div>
      )}

      {/* 3-column grid: left toolbox, canvas, right inspector */}
      <div className="grid grid-cols-1 xl:grid-cols-[240px_1fr_300px] gap-4">
        {/* Left */}
        <div className="space-y-3" data-testid="designer-left-pane">
          <AddSectionToolbar disabled={disabled} onAdd={addSection} />
          <PricingZoneManager
            templateId={templateId}
            zones={zones}
            disabled={disabled}
            onChange={load}
          />
          <button
            type="button"
            onClick={() => setWizardOpen(true)}
            disabled={disabled}
            className="gt-btn-ghost text-xs w-full justify-center"
            data-testid="designer-open-wizard-btn"
          >
            <Sparkles size={12} /> Preset wizard
          </button>
        </div>

        {/* Canvas */}
        <div className="relative border border-[var(--gt-line)] rounded-sm bg-[var(--gt-bg-surface)] p-2">
          <div className="flex items-center justify-between mb-2 px-1">
            <div className="text-[10px] uppercase tracking-widest text-[var(--gt-ink-3)]">
              Canvas · 100 × 100 · drag / resize / rotate — arrow keys nudge
            </div>
            <div className="flex items-center gap-2">
              {selectedIds.length > 0 && !disabled && (
                <>
                  <button
                    type="button"
                    onClick={duplicateSelected}
                    className="gt-btn-ghost text-[10px]"
                    data-testid="designer-duplicate-selected-btn"
                  >
                    <Copy size={11} /> Duplicate
                  </button>
                  <button
                    type="button"
                    onClick={deleteSelected}
                    className="gt-btn-ghost text-[10px] text-[var(--gt-accent)]"
                    data-testid="designer-delete-selected-btn"
                  >
                    <Trash2 size={11} /> Delete
                  </button>
                </>
              )}
              {saving && (
                <span className="inline-flex items-center gap-1 text-[10px] text-[var(--gt-ink-3)]" data-testid="designer-saving-indicator">
                  <Loader2 size={10} className="animate-spin" /> Saving
                </span>
              )}
            </div>
          </div>
          <div
            ref={canvasRef}
            className="relative w-full aspect-square bg-white border border-[var(--gt-line)] rounded-sm overflow-hidden select-none"
            data-testid="designer-canvas"
          >
            {/* Grid overlay */}
            <svg
              className="absolute inset-0 pointer-events-none"
              viewBox={`0 0 ${VW} ${VH}`}
              preserveAspectRatio="none"
            >
              {Array.from({ length: 21 }).map((_, i) => (
                <React.Fragment key={i}>
                  <line x1={i * 5} y1={0} x2={i * 5} y2={VH} stroke="rgba(0,0,0,0.04)" strokeWidth={0.05} />
                  <line x1={0} y1={i * 5} x2={VW} y2={i * 5} stroke="rgba(0,0,0,0.04)" strokeWidth={0.05} />
                </React.Fragment>
              ))}
            </svg>

            {/* Section nodes */}
            {sections.map((s) => {
              const zone = s.pricing_zone_id ? zonesById[s.pricing_zone_id] : null;
              const isDecor = !!s.is_decorative;
              const bg = isDecor ? "rgba(9,9,11,0.85)" : (zone?.hex_colour || "rgba(9,9,11,0.15)");
              const fg = isDecor ? "#fff" : (zone ? "#fff" : "var(--gt-ink)");
              const isSelected = selectedIds.includes(s.id);
              return (
                <div
                  key={s.id}
                  data-section-node={s.id}
                  data-testid={`canvas-section-${s.name}`}
                  data-selected={isSelected ? "true" : "false"}
                  className="absolute origin-center flex items-center justify-center font-bold text-center overflow-hidden"
                  style={{
                    left: `${s.x_percent ?? 0}%`,
                    top: `${s.y_percent ?? 0}%`,
                    width: `${s.width_percent ?? 10}%`,
                    height: `${s.height_percent ?? 10}%`,
                    transform: `rotate(${s.rotation_deg || 0}deg)`,
                    background: bg,
                    color: fg,
                    border: isSelected ? "2px solid var(--gt-ink)" : "1px solid rgba(0,0,0,0.15)",
                    borderRadius: `${(s.border_radius_percent ?? 8) * 0.6}%`,
                    cursor: disabled ? "default" : "move",
                    fontSize: "clamp(9px, 1.4vw, 14px)",
                    letterSpacing: "0.5px",
                    padding: "4px",
                  }}
                  onMouseDown={(e) => {
                    if (disabled) return;
                    // multi-select with shift/meta
                    if (e.shiftKey || e.metaKey || e.ctrlKey) {
                      setSelectedIds((cur) => cur.includes(s.id) ? cur.filter((x) => x !== s.id) : [...cur, s.id]);
                    } else if (!isSelected) {
                      setSelectedIds([s.id]);
                    }
                  }}
                >
                  {(s.label_display || "always") !== "hidden" && (
                    <span style={{ pointerEvents: "none" }}>{s.name}</span>
                  )}
                </div>
              );
            })}

            {/* Moveable handles for the currently-selected sections */}
            {selectedIds.length > 0 && !disabled && (
              <Moveable
                ref={moveableRef}
                target={targetsFromSelected()}
                draggable
                resizable
                rotatable
                keepRatio={false}
                origin={false}
                snappable
                snapGridWidth={GRID}
                snapGridHeight={GRID}
                onDrag={onDrag}
                onDragEnd={onDragEnd}
                onResize={onResize}
                onResizeEnd={onResizeEnd}
                onRotate={onRotate}
                onRotateEnd={onRotateEnd}
              />
            )}

            <Selecto
              container={canvasRef.current}
              selectableTargets={["[data-section-node]"]}
              hitRate={0}
              selectByClick={false}
              selectFromInside={false}
              continueSelect={false}
              toggleContinueSelect={"shift"}
              ratio={0}
              onSelect={(e) => {
                const ids = (e.selected || []).map((el) => el.getAttribute("data-section-node")).filter(Boolean);
                setSelectedIds(ids);
              }}
            />
          </div>
        </div>

        {/* Right */}
        <div className="space-y-3" data-testid="designer-right-pane">
          <SectionPropertiesPanel
            section={selectedSection}
            zones={zones}
            disabled={disabled}
            onPatch={(patch) => selectedSection && patchSection(selectedSection.id, patch)}
            onDelete={deleteSelected}
            onDuplicate={duplicateSelected}
          />
          {selectedIds.length > 1 && (
            <div
              className="text-[10px] text-[var(--gt-ink-3)] p-2 border border-dashed border-[var(--gt-line)] rounded-sm"
              data-testid="multi-select-hint"
            >
              {selectedIds.length} sections selected — drag to move all, arrow keys to nudge, or apply zone below.
              <select
                onChange={(e) => applyPatchToSelected({ pricing_zone_id: e.target.value || null })}
                defaultValue=""
                className="gt-input w-full mt-2 text-xs"
                data-testid="multi-select-zone-select"
              >
                <option value="">Assign zone…</option>
                {zones.map((z) => (
                  <option key={z.id} value={z.id}>{z.name}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      <PresetWizard open={wizardOpen} onClose={() => setWizardOpen(false)} onPick={applyPreset} />
      <BuyerPreviewModal open={previewOpen} templateId={templateId} onClose={() => setPreviewOpen(false)} />
    </div>
  );
}
